import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { apiInstance } from "@/shared/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import type { PaginatedResponse, PaginationRequest } from "@/shared/types";
import type { User } from "@/entities/user";
import type { ManagerV2 } from "../model";
import type {
  AssignTeamleadToManagerRequest,
  AssignTeamleadToManagerResponse,
  TransferManagerToTeamleadRequest,
} from "./api.dto";

export const useAssignTeamleadToManager = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AssignTeamleadToManagerResponse,
    AxiosError,
    AssignTeamleadToManagerRequest
  >({
    mutationKey: ["assignTeamleadToManager"],
    mutationFn: (data) => {
      return apiInstance.post(
        `/managers/${data.managerId}/assign-teamlead`,
        { teamleadId: data.teamleadId },
        undefined,
        true,
      );
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ["managers"],
      });
    },
  });
};

export const useTransferManagerToTeamlead = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AssignTeamleadToManagerResponse,
    AxiosError,
    TransferManagerToTeamleadRequest
  >({
    mutationKey: ["transferTeamleadToManager"],
    mutationFn: (data) => {
      return apiInstance.post(
        `/managers/${data.managerId}/transfer-to-teamlead`,
        { teamleadId: data.teamleadId },
        undefined,
        true,
      );
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ["managers"],
      });
    },
  });
};

export const useGetAllManagers = (
  params: PaginationRequest & { enabled?: boolean },
) => {
  return useQuery<PaginatedResponse<ManagerV2>>({
    queryKey: ["managers", filterObjectKeys(params, ["enabled"])],
    queryFn: async () => {
      const { data } = await apiInstance.get<PaginatedResponse<ManagerV2>>(
        "/managers",
        filterObjectKeys(params, ["enabled"]),
        true,
      );

      return data;
    },
  });
};

export const useGetAllManagersInf = (
  params: Omit<PaginationRequest, "currentPage">,
  { enabled }: { enabled: boolean },
) => {
  return useInfiniteQuery<PaginatedResponse<ManagerV2>>({
    queryKey: ["managers", filterObjectKeys(params)],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await apiInstance.get<PaginatedResponse<ManagerV2>>(
        "/managers",
        {
          ...filterObjectKeys(params),
          currentPage: pageParam,
        },
        true,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total;
      const itemsPerPage = 5;
      const currentPage = allPages.length;

      const totalPages = Math.ceil(totalItems / itemsPerPage);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    enabled,
  });
};

export const useGetManagersByTeamlead = (
  params: PaginationRequest & { userId: string; enabled?: boolean },
) => {
  const filteredParams = filterObjectKeys(params, ["enabled"]);

  return useQuery<PaginatedResponse<ManagerV2>>({
    queryKey: ["managers", "teamlead", filteredParams],
    queryFn: async () => {
      const { data } = await apiInstance.get<PaginatedResponse<ManagerV2>>(
        `/managers/teamlead/${params.userId}`,
        filteredParams,
        true,
      );

      return data;
    },
    enabled: params.enabled,
  });
};

export const useGetManagersByTeamleadInf = (
  params: Omit<PaginationRequest & { userId: string }, "currentPage">,
  { enabled }: { enabled: boolean },
) => {
  return useInfiniteQuery<PaginatedResponse<ManagerV2>>({
    queryKey: ["managers", "teamlead", filterObjectKeys(params, ["userId"])],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await apiInstance.get<PaginatedResponse<ManagerV2>>(
        `/managers/teamlead/${params.userId}`,
        {
          ...filterObjectKeys(params, ["userId"]),
          currentPage: pageParam,
        },
        true,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total;
      const itemsPerPage = 5;
      const currentPage = allPages.length;

      const totalPages = Math.ceil(totalItems / itemsPerPage);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    enabled,
  });
};

export const useGetManagersByMasterteamlead = (
  params: PaginationRequest & { userId: string; enabled?: boolean },
) => {
  return useQuery<PaginatedResponse<ManagerV2>>({
    queryKey: [
      "managers",
      "masterTeamlead",
      filterObjectKeys(params, ["enabled"]),
    ],
    queryFn: async () => {
      const { data } = await apiInstance.get<
        PaginatedResponse<ManagerV2 & { userId: User; teamlead: string }>
      >(
        `/managers/master-teamlead/${params.userId}`,
        filterObjectKeys(params, ["userId", "enabled"]),
        true,
      );

      return data;
    },
    enabled: params.enabled,
  });
};

export const useGetManagersByMasterTeamleadInf = (
  params: Omit<PaginationRequest & { userId: string }, "currentPage">,
  { enabled }: { enabled: boolean },
) => {
  return useInfiniteQuery<PaginatedResponse<ManagerV2>>({
    queryKey: [
      "managers",
      "masterTeamlead",
      filterObjectKeys(params, ["userId"]),
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await apiInstance.get<PaginatedResponse<ManagerV2>>(
        `/managers/master-teamlead/${params.userId}`,
        {
          ...filterObjectKeys(params, ["userId"]),
          currentPage: pageParam,
        },
        true,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total;
      const itemsPerPage = 5;
      const currentPage = allPages.length;

      const totalPages = Math.ceil(totalItems / itemsPerPage);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    enabled,
  });
};
